import {
    TaskParticipationResource,
    TeamParticipationResource,
} from 'dy-frontend-http-repository/lib/modules/Task/resources';
import { CreateTimeTrackingEntryInput } from 'dy-frontend-http-repository/lib/modules/TimeTacking/inputs';
import { TimeTrackingEntryResource } from 'dy-frontend-http-repository/lib/modules/TimeTacking/resources';
import { ImageHashPreviewSize } from 'dy-frontend-shared/lib/data/valueObjects/ImageHashPreview/enums';
import { useStore } from 'effector-react';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

import Avatar from '@app/components/Avatar';
import Circle from '@app/components/Circle';
import Grid from '@app/components/Grid';
import SelectFormField from '@app/components/SelectFormField';
import DevText from '@app/components/Text';
import TextAreaFormField from '@app/components/TextAreaFormField';
import { $task } from '@app/containers/pages/Task/store/states';
import { $authorizedUser } from '@app/containers/store/states';
import { imageHashPreview } from '@app/data/consts';
import { workReportActorSwitchRoles } from '@app/data/consts/roleFeatureAccess';
import { useCustomSelectFormField, useForm, useTextFormField } from '@app/hooks';
import { getStringMaxLengthValidator, getStringRequiredValidator } from '@app/hooks/validation/functions';
import { Button, Callout, Card, Classes, Colors, Divider, FormGroup, Intent, Popover, Tag } from '@blueprintjs/core';
import { DateInput3 } from '@blueprintjs/datetime2';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import Overlay from '@components/Overlay';
import { ModalProps } from '@modals/types';

import { workReportHoursList, workReportMinutesList, workReportTypesInformation } from '../../consts';
import { timeTrackingEntriesApi } from '../../store/apis';
import { createWorkReports } from '../../store/effects';

import {
    BACKDATED_WORK_REPORT_COMMENT_MESSAGE,
    isBackdatedExplanationMissing,
    isWorkDateBackdated,
} from './utils/backdatedWorkReport';
import { isDirectorWorkReportSwitch, isWorkReportSwitchCandidateOffered } from './utils/directorWorkReportSwitch';
import {
    TIME_OFF_WORK_REPORT_COMMENT_MESSAGE,
    isTimeOffExplanationMissing,
    isTimeOffWorkReportType,
} from './utils/timeOffWorkReport';

import { SelectedActorUser } from './types';

export interface CreateWorkReportModalProps {
    taskId: ID;
    actorUserId: ID;

    // Optional sub-task attribution. When present the entry carries `task_sub_ticket_id`, which
    // is the same foreign key the timer finalizer writes — one ledger, no second time table.
    subTicketId?: ID;

    // Immutable `#N` label, shown so the reporter can see which phase they are reporting against
    subTicketOrdinal?: number;

    // Sub-task assignee, preselected for a manager-created entry. The work belongs to the
    // designer who did it, not to the manager recording it; the existing Switch control is
    // untouched, so this is a default and not a restriction.
    subTicketAssignee?: SelectedActorUser;

    // Refreshes whatever view opened the modal once the entry has committed
    onCreated?: () => void;
}

type Props = ModalProps<CreateWorkReportModalProps>;

const commentValidators = [getStringMaxLengthValidator({ maxStringLength: 250 })];
const workReportTypeValidators = [getStringRequiredValidator()];
const hoursValidators = [getStringRequiredValidator()];
const minutesValidators = [getStringRequiredValidator()];

// The date the work is booked against. Defaults to today and stays editable, because work is
// routinely reported the morning after it was done and a hardcoded `now` silently books it on the
// wrong day. Formatted in UTC to match the `date_at` DATE column the ledger stores.
export const resolveWorkReportDate = (workDate: Date | null): string =>
    moment(workDate ?? undefined)
        .utc()
        .format('YYYY-MM-DD');

const CreateWorkReportModal: React.FC<Props> = ({ closeModal, data }) => {
    const task = useStore($task);
    const me = useStore($authorizedUser);

    const [workDate, setWorkDate] = useState<Date | null>(() => new Date());

    const comment = useTextFormField({ id: 'comment', validators: commentValidators, initialValue: '' });

    const workReportType = useCustomSelectFormField<string | null>({
        id: 'work-report-type',
        validators: workReportTypeValidators,
        initialValue: '',
        formatValue: (value) => value ?? '',
    });

    const hours = useCustomSelectFormField<string | null>({
        id: 'hours',
        validators: hoursValidators,
        initialValue: '',
        formatValue: (value) => value ?? '',
    });

    const minutes = useCustomSelectFormField<string | null>({
        id: 'minutes',
        validators: minutesValidators,
        initialValue: '',
        formatValue: (value) => value ?? '',
    });

    const form = useForm({
        fields: [comment, hours, minutes, workReportType],

        apiCall: async () => {
            try {
                // The disabled button is the courtesy; this is the rule. Anything that reaches
                // the submit path with a backdated date and no explanation is refused here,
                // before the entry is posted — the server refuses it too, and the two must not
                // be able to disagree
                if (isBackdatedExplanationMissing(workDate, comment.value, new Date())) {
                    throw new Error(BACKDATED_WORK_REPORT_COMMENT_MESSAGE);
                }

                // Same for a day reported off. It carries no duration and nothing else at all, so
                // the reason is the only thing that makes it reconstructable later
                if (isTimeOffExplanationMissing(workReportType.value, comment.value)) {
                    throw new Error(TIME_OFF_WORK_REPORT_COMMENT_MESSAGE);
                }

                // Create input
                const createTimeCreatingEntryInput: CreateTimeTrackingEntryInput = {
                    actor_user_id: selectedActor!.id,
                    comment: comment.value,
                    type: workReportType.value ?? 'unknown',
                    date_at: resolveWorkReportDate(workDate),
                    task_id: data!.taskId,
                    duration_min: parseInt(hours.value ?? '0') * 60 + parseInt(minutes.value ?? '0'),
                    // Only when the modal was opened from a sub-task card; an ordinary
                    // Request-level report carries no phase at all
                    ...(data!.subTicketId ? { task_sub_ticket_id: data!.subTicketId } : {}),
                };

                // Create time tracking entry
                const entryRef = await createWorkReports(createTimeCreatingEntryInput);

                // Add newly created time tracking entry to local entries state
                const authorizedUser = me?.user;

                if (authorizedUser) {
                    // Get current date in UTC format
                    const momentNowUtc = moment().utc().format();

                    // Create entry object
                    const newEntry: TimeTrackingEntryResource = {
                        id: entryRef.id,
                        actor_user: {
                            first_name: selectedActor!.firstName,
                            last_name: selectedActor!.lastName,
                            id: selectedActor!.id,
                            image_hash: selectedActor!.imageHash,
                        },
                        archived_at: null,
                        author_user_id: authorizedUser.id,
                        comment: createTimeCreatingEntryInput.comment,
                        created_at: momentNowUtc,
                        date_at: createTimeCreatingEntryInput.date_at,
                        duration_min: createTimeCreatingEntryInput.duration_min,
                        task: { id: task?.id ?? 'Unknown ID', title: task?.title ?? 'Unknown title' },
                        updated_at: momentNowUtc,
                        type: createTimeCreatingEntryInput.type,
                    };

                    // Add newly created entry to local entries state
                    timeTrackingEntriesApi.add(newEntry);
                }

                // Let the caller refresh whatever it is showing — the sub-task row's logged
                // total and the aside summary are both server-derived
                data!.onCreated?.();

                // Close modal
                closeModal?.();

                return { response: true };
            } catch (e) {
                throw e;
            }
        },
        onFailure: (error) => {
            // TODO: handle error
            console.error(error);
        },
    });

    const [selectedActor, setSelectedActor] = useState<SelectedActorUser | null>(null);

    // Recomputed on render rather than memoised: the answer depends on the wall clock, and a
    // modal left open across midnight must start asking for the explanation it now needs
    const currentDate = new Date();
    const backdatedWorkDateSelected = isWorkDateBackdated(workDate, currentDate);
    const backdatedExplanationRequired = isBackdatedExplanationMissing(workDate, comment.value, currentDate);

    const timeOffWorkReportSelected = isTimeOffWorkReportType(workReportType.value);
    const timeOffExplanationRequired = isTimeOffExplanationMissing(workReportType.value, comment.value);

    // A day off has no duration. The selects are withheld below, but the values behind them are
    // zeroed rather than merely hidden: the payload expression reads them either way, and a
    // duration picked before the type was switched would otherwise still be posted.
    useEffect(() => {
        if (timeOffWorkReportSelected) {
            hours.handleChange('0');
            minutes.handleChange('0');
            return;
        }

        hours.reset();
        minutes.reset();
    }, [timeOffWorkReportSelected]);

    useEffect(() => {
        if (me === null) {
            return;
        }

        if (selectedActor === null) {
            // Reporting against a sub-task defaults to the designer the phase is assigned to:
            // the work is theirs even when a manager is the one recording it. The Switch control
            // below is untouched, so this is a default and never a restriction.
            const initialActor = data?.subTicketAssignee ?? {
                id: me.user.id,
                firstName: me.user.first_name,
                lastName: me.user.last_name,
                imageHash: me.user.image_hash,
            };

            setSelectedActor(initialActor);
        }
    }, [me, data?.subTicketAssignee]);

    if (!data || !me) {
        return null;
    }

    const renderSelectParticipantPopoverContent = (participants: TaskParticipationResource[]) => {
        const isDirectorSwitch = isDirectorWorkReportSwitch(me.user.role);

        const renderTeamParticipation = (teamParticipation: TeamParticipationResource[]) => {
            if (teamParticipation.length === 0) {
                return (
                    <Flex align="center">
                        <Circle className="mr-1" size="12px" color="none" borderColor={Colors.BLACK} />
                        <DevText muted>No team</DevText>
                    </Flex>
                );
            }

            return teamParticipation.map((tp, index) => (
                <Flex key={tp.assigned_at} align="center" className={index !== 0 ? 'mt-small' : ''}>
                    <Circle className="mr-1" size="12px" borderColor="none" color={tp.team.color} />
                    <DevText muted>{tp.team.title}</DevText>
                </Flex>
            ));
        };

        // Get avatar src
        let avatarSrc: string | null = null;
        if (me.user.image_hash) {
            avatarSrc = imageHashPreview.userImage(me.user.image_hash, ImageHashPreviewSize.XS);
        }

        return (
            <Card compact>
                {/* The viewer's own row. A director does not report for themselves through a
                    control meant for the people they direct — and does not need to: they are
                    already the default actor. */}
                {!isDirectorSwitch && (
                    <Flex direction="row" align="center" justify="space-between">
                        <Flex className="mr-2" direction="row" align="center">
                            <Avatar
                                className="mr-2"
                                width="35px"
                                height="35px"
                                alt={`${me.user.first_name} ${me.user.last_name}`}
                                src={avatarSrc}
                            />
                            <DevText>
                                {me.user.first_name} {me.user.last_name}
                            </DevText>
                        </Flex>

                        <Button
                            minimal
                            disabled={selectedActor !== null && selectedActor.id === me.user.id}
                            icon="plus"
                            className={Classes.POPOVER_DISMISS}
                            intent={Intent.SUCCESS}
                            onClick={() =>
                                setSelectedActor({
                                    id: me.user.id,
                                    firstName: me.user.first_name,
                                    lastName: me.user.last_name,
                                    imageHash: me.user.image_hash,
                                })
                            }
                        />
                    </Flex>
                )}

                {participants.map((participation) => {
                    const { id, first_name, last_name, image_hash, team_participation } = participation.user;

                    // Get avatar src
                    let avatarSrc: string | null = null;
                    if (image_hash) {
                        avatarSrc = imageHashPreview.userImage(image_hash, ImageHashPreviewSize.XS);
                    }

                    return (
                        <Flex key={participation.user.id} className="mt-2" justify="space-between">
                            <Flex className="mr-2" direction="row">
                                <Avatar
                                    className="mr-2"
                                    width="35px"
                                    height="35px"
                                    alt={`${first_name} ${last_name}`}
                                    src={avatarSrc}
                                />

                                <Flex direction="column" align="flex-start" justify="center">
                                    <DevText>
                                        {first_name} {last_name}
                                    </DevText>

                                    {renderTeamParticipation(team_participation)}
                                </Flex>
                            </Flex>

                            <Button
                                minimal
                                disabled={selectedActor !== null && selectedActor.id === id}
                                icon="plus"
                                className={Classes.POPOVER_DISMISS}
                                intent={Intent.SUCCESS}
                                onClick={() =>
                                    setSelectedActor({
                                        id,
                                        firstName: first_name,
                                        lastName: last_name,
                                        imageHash: image_hash,
                                    })
                                }
                            />
                        </Flex>
                    );
                })}
            </Card>
        );
    };

    const renderSelectedActorCard = () => {
        if (!selectedActor) {
            return <div className={Classes.SKELETON} style={{ background: Colors.DARK_GRAY2, height: '75px' }}></div>;
        }

        // Get avatar srcD
        let avatarSrc: string | null = null;
        if (selectedActor.imageHash) {
            avatarSrc = imageHashPreview.userImage(selectedActor.imageHash, ImageHashPreviewSize.XS);
        }

        // Get actor name
        const fullName = `${selectedActor.firstName} ${selectedActor.lastName}`;

        // Who this viewer may report for. Every role the control was already open to keeps the
        // whole participant list; a director is offered only the designers they share a team with
        const switchableParticipants = (task?.participants ?? []).filter((participation) =>
            isWorkReportSwitchCandidateOffered(
                { userId: me.user.id, role: me.user.role, teamIds: (me.team ?? []).map((team) => team.id) },
                {
                    userId: participation.user.id,
                    role: participation.user.role,
                    teamIds: (participation.user.team_participation ?? []).map((tp) => tp.team.id),
                }
            )
        );

        return (
            <Card compact style={{ background: Colors.DARK_GRAY2 }}>
                <Flex direction="row" justify="space-between">
                    <Flex direction="row" align="center">
                        <Avatar className="mr-1" width="35px" height="35px" src={avatarSrc} alt={fullName} />
                        <DevText>
                            {fullName}

                            {me.user.id === selectedActor.id && (
                                <DevText inline muted className="ml-1">
                                    (You)
                                </DevText>
                            )}
                        </DevText>
                    </Flex>

                    {switchableParticipants.length > 0 && (
                        <Popover content={renderSelectParticipantPopoverContent(switchableParticipants)}>
                            <Button className={Classes.POPOVER_DISMISS} icon="swap-vertical">
                                Switch
                            </Button>
                        </Popover>
                    )}
                </Flex>
            </Card>
        );
    };

    const renderWorkReportForUserField = () => {
        const isUserSwitchAllowed = workReportActorSwitchRoles.includes(me?.user.role);
        if (!isUserSwitchAllowed) {
            return null;
        }

        return (
            <div className="mb-2">
                <FormGroup label="Report for">{renderSelectedActorCard()}</FormGroup>
            </div>
        );
    };

    return (
        <Overlay isOpen onClose={closeModal}>
            <Card style={{ width: '558px' }}>
                <Flex className="mb-2" align="center" justify="space-between">
                    <Flex direction="row" align="center">
                        <Heading className="mr-1" type="h4">
                            Create work report
                        </Heading>

                        {data.subTicketOrdinal !== undefined && (
                            <Tag minimal icon="layers">
                                Sub-task #{data.subTicketOrdinal}
                            </Tag>
                        )}
                    </Flex>

                    <Button minimal icon="cross" onClick={closeModal} />
                </Flex>

                <Divider className="mb-2" />

                <form onSubmit={form.handleFormSubmit}>
                    {/* Report for */}
                    {renderWorkReportForUserField()}

                    {/* Report Type */}
                    <SelectFormField
                        field={workReportType}
                        items={Object.keys(workReportTypesInformation)}
                        formGroupProps={{ label: 'Report Type' }}
                        selectProps={{ selectButtonProps: { placeholder: 'Select report type', icon: null } }}
                        itemToReadableFormatter={(item) => workReportTypesInformation[item]}
                    />

                    {/* Work date */}
                    <FormGroup label="Work date">
                        <DateInput3
                            fill
                            highlightCurrentDay
                            placeholder="Pick the date the work was done"
                            value={workDate ? moment(workDate).format() : null}
                            dayPickerProps={{ firstWeekContainsDate: 1 }}
                            formatDate={(date) => moment(date).format('MMM Do, YYYY (dddd)')}
                            parseDate={(str) => new Date(str)}
                            onChange={(newDate) => setWorkDate(newDate ? new Date(newDate) : null)}
                        />
                    </FormGroup>

                    {/* Duration. Withheld for a day off: there is no duration to report, and the
                        values behind these selects are zeroed when the type is chosen */}
                    {!timeOffWorkReportSelected && (
                        <Grid container>
                            <Grid xs={6} lg={6}>
                                <SelectFormField
                                    field={hours}
                                    items={workReportHoursList}
                                    formGroupProps={{ label: 'Hours worked' }}
                                    selectProps={{
                                        selectButtonProps: { placeholder: 'Select hours being worked', icon: null },
                                    }}
                                />
                            </Grid>

                            <Grid xs={6} lg={6}>
                                <SelectFormField
                                    field={minutes}
                                    items={workReportMinutesList}
                                    formGroupProps={{ label: 'Minutes worked' }}
                                    selectProps={{
                                        selectButtonProps: { placeholder: 'Select minutes being worked', icon: null },
                                    }}
                                    itemToReadableFormatter={(item) => item.padStart(2, '0')}
                                />
                            </Grid>
                        </Grid>
                    )}

                    {/* Comment */}
                    <TextAreaFormField
                        field={comment}
                        formGroupProps={{ label: 'Comment' }}
                        textAreaProps={{ placeholder: 'Enter comment' }}
                    />

                    {/* Why a past date needs a note. Shown verbatim: this is the sentence the
                        API returns, and a paraphrase here would be a second, quieter rule */}
                    {backdatedWorkDateSelected && (
                        <Callout intent={Intent.WARNING} icon="calendar" className="mb-2">
                            <DevText>{BACKDATED_WORK_REPORT_COMMENT_MESSAGE}</DevText>
                        </Callout>
                    )}

                    {/* Why a day off needs a note. Shown verbatim, for the same reason: this is the
                        sentence the API returns */}
                    {timeOffWorkReportSelected && (
                        <Callout intent={Intent.WARNING} icon="moon" className="mb-2">
                            <DevText>{TIME_OFF_WORK_REPORT_COMMENT_MESSAGE}</DevText>
                        </Callout>
                    )}

                    <Flex className="mt-2" justify="flex-end">
                        <Button className="mr-1" outlined onClick={closeModal}>
                            Cancel
                        </Button>

                        <Button
                            disabled={
                                form.hasFieldErrors ||
                                selectedActor === null ||
                                workDate === null ||
                                backdatedExplanationRequired ||
                                timeOffExplanationRequired
                            }
                            loading={form.isSubmitting}
                            type="submit"
                            intent={Intent.PRIMARY}
                        >
                            Create
                        </Button>
                    </Flex>
                </form>
            </Card>
        </Overlay>
    );
};

export default CreateWorkReportModal;
